'use client';
import React, {
    Dispatch,
    FunctionComponent,
    SetStateAction,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import type { TableProps } from 'antd';
import { ConfigProvider, Table } from 'antd';
import { Box } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { BasePartParameterValue } from '../../../schemas/BasePartParameterValue';
import CustomPagination from '../../common/CustomPagination';
import { useLocation, useNavigate } from 'react-router-dom';
import { DetectedUniquePart } from '../../../schemas/DetectedUniquePart';
import { useLazyGetBasePartByPnQuery } from '../../../state/querySlice';
import styled from 'styled-components';
import { useLazyGetParametersByDefsQuery } from '../../../state/treeSlice';
import { DetectedDefinition } from '../../../schemas/DetectedDefinition';
import { useLocale } from '../../../contexts/LocaleProvider';
import { LanguageMapping } from '../../../utils/LanguageMapping';
import { OctoPartRequest } from '../../../schemas/OctoPartRequest';
import { useLazyGetOctoMultiPartQuery } from '../../../state/multipartSlice';
import { Alias } from '../../../schemas/Alias';
import { useProductTableColumns } from '../hooks/useProductTableColumns';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';

const ActionComponentDisabled = () => <span />;

const StyledTable = styled((props: TableProps<BasePartParameterValue>) => <Table {...props} />)`
    /* 修改 header 的高度為 53px */

    .ant-table-thead > tr > th {
        height: 53px;
        line-height: 1.2;
        padding: 0; /* 將 padding 改成 0.5rem */
        white-space: normal; /* 允許換行 */
        word-break: break-word;
        /*
           }
       
           .ant-table-tbody > tr > td {
               height: 100px;       /* 固定高度 */
        padding: 0;
    }

    .ant-table-tbody > tr > td.ant-table-cell-fix-left,
    .ant-table-tbody > tr > td.ant-table-cell-fix-left-last {
        padding: 0.5rem 0.5rem 0.5rem 0.5rem; /* ← 這裡想要的數值 */
    }
`;

export function convertArrayToOctoPartRequests(
    baseParts: BasePartParameterValue[]
): OctoPartRequest[] {
    return baseParts.map((basePart) => {
        const aliases: Alias[] | null = basePart.alias
            ? [{ displayable: true, value: basePart.alias }]
            : null;

        return {
            uniquePn: basePart.displayPn,
            aliases,
        };
    });
}

interface ProductTableProps {
    basePartResults: BasePartParameterValue[];
    partCount: number;
    rowsPerPage: number;
    setRowsPerPage: Dispatch<SetStateAction<number>>;
    treeName: string;
    ascColumn: string;
    setAscColumn: Dispatch<SetStateAction<string>>;
    descColumn: string;
    setDescColumn: Dispatch<SetStateAction<string>>;
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    isTableLoading: boolean;
    setIsNumericSort: Dispatch<SetStateAction<boolean>>;
    detectedUniqueParts: DetectedUniquePart[] | undefined;
    setIsBasePartTableLoading: Dispatch<SetStateAction<boolean>>;
    levelId: number[] | undefined;
    definitionsMap: Map<string, Map<string, DetectedDefinition>>;
    compareItemPns: string[];
    onToggleCompareItem: (pn: string, checked: boolean, full: BasePartParameterValue) => void;
    contentTree: ProductsContent;
}

const HEADER_TOP = 96;

const TableWrapper = styled.div`
    .ant-table-header {
        top: ${HEADER_TOP}px;
        z-index: 10;
        background: #b75332; /* 跟 headerBg 一樣的顏色 */
    }

    .ant-table-cell-row-hover [data-testid$='_zone']:hover {
        cursor: default !important;
    }

    td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last.ant-table-cell-row-hover:hover {
        cursor: default !important;
    }
`;

const ProductTable: FunctionComponent<ProductTableProps> = ({
    basePartResults,
    partCount,
    rowsPerPage,
    setRowsPerPage,
    treeName,
    ascColumn,
    setAscColumn,
    descColumn,
    setDescColumn,
    page,
    setPage,
    isTableLoading,
    setIsNumericSort,
    detectedUniqueParts,
    setIsBasePartTableLoading,
    levelId,
    definitionsMap,
    compareItemPns,
    onToggleCompareItem,
    contentTree,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useLocale();
    const language = LanguageMapping.get(locale) || 'ENGLISH';

    const [triggerPnQuery, { data: basePartsData, isLoading: isPnLoading }] =
        useLazyGetBasePartByPnQuery();
    const [triggerOctopartQuery, { data: multiOctoPartsResponse, isLoading: isOctoLoading }] =
        useLazyGetOctoMultiPartQuery();

    // 將 detectedUniqueParts 轉成 Map，key = uniquePn
    const detectedUniquePartsMap = useMemo(() => {
        if (!detectedUniqueParts) {
            return {} as Record<string, DetectedUniquePart>;
        }
        return detectedUniqueParts.reduce(
            (acc, dup) => {
                acc[dup.uniquePn] = dup;
                return acc;
            },
            {} as Record<string, DetectedUniquePart>
        );
    }, [detectedUniqueParts]);

    useEffect(() => {
        const parts: OctoPartRequest[] = convertArrayToOctoPartRequests(basePartResults);
        triggerOctopartQuery(parts)
            .then(() => setIsBasePartTableLoading(true))
            .finally(() => setIsBasePartTableLoading(false));
    }, [basePartResults]);
    useEffect(() => {
        if (detectedUniqueParts && detectedUniqueParts.length > 0) {
            // 1) 先計算出分頁起始與結束索引
            const offset = page * rowsPerPage;
            // 2) 只拿對應分頁要顯示的 unique PNs
            const slicedUniqueParts = detectedUniqueParts.slice(offset, offset + rowsPerPage);
            const pnArray = slicedUniqueParts.map((dup) => dup.uniquePn);
            // 直接呼叫 API，取得對應的 basePart資料
            triggerPnQuery(pnArray)
                .then(() => setIsBasePartTableLoading(true))
                .finally(() => setIsBasePartTableLoading(false));
        }
    }, [detectedUniqueParts, triggerPnQuery, page, rowsPerPage]);

    /**
     * 1.quality / sum 雙指標
     *    { [uniquePn]: { q: matchQuality, s: matchSortingParameterSum } }
     */
    const pnMetricMap = useMemo(() => {
        if (!detectedUniqueParts?.length) return {} as Record<string, { q: number; s: number }>;

        return detectedUniqueParts.reduce(
            (acc, dup) => {
                acc[dup.uniquePn] = { q: dup.matchQuality, s: dup.matchSortingParameterSum };
                return acc;
            },
            {} as Record<string, { q: number; s: number }>
        );
    }, [detectedUniqueParts]);

    /**
     * 2. effectiveBaseParts：原本邏輯不動
     */
    const effectiveBaseParts = useMemo(() => {
        if (detectedUniqueParts && detectedUniqueParts.length > 0) {
            // 如果 Query 還沒回應，就先給空陣列
            return basePartsData ?? [];
        }

        return basePartResults;
    }, [detectedUniqueParts, basePartsData, basePartResults]);

    // console.log(
    //     '[effectiveBaseParts]',
    //     detectedUniqueParts?.length,
    //     basePartsData?.length,
    //     effectiveBaseParts.length
    // );

    /**
     * 3. sortedBaseParts：只有在 detectedUniqueParts 存在時才根據 pnScoreMap 排序
     */
    const sortedBaseParts = useMemo(() => {
        if (!detectedUniqueParts?.length) return effectiveBaseParts;

        return [...effectiveBaseParts].sort((a, b) => {
            const { q: qa = 0, s: sa = 0 } = pnMetricMap[a.basePn] ?? {};
            const { q: qb = 0, s: sb = 0 } = pnMetricMap[b.basePn] ?? {};

            // 1️⃣ Match Quality 由大到小
            if (qb !== qa) return qb - qa;
            // 2️⃣ 相同時再比較 MatchSortingParameterSum
            return sb - sa;
        });
    }, [effectiveBaseParts, detectedUniqueParts, pnMetricMap]);

    // 先從 definitionsMap 取得 defId
    const defId = definitionsMap.get(treeName)?.get(treeName)?.id || 1;
    // 如果 levelId 不存在或為空，就以 defId 當作預設的 subdef
    const subdefIds = levelId && levelId.length > 0 ? levelId : [defId];

    const [
        triggerParams, // 呼叫它才送 request
        { data: parameters, isFetching: parametersLoading },
    ] = useLazyGetParametersByDefsQuery();

    const search = useLocation().search;
    const q79 = new URLSearchParams(search).get('79') ?? ''; // 沒有就空字串

    const paramKey = `${defId}-${subdefIds.join('|')}-${q79}`;

    // 追蹤上一次送出的 key
    const prevKeyRef = useRef<string>();

    // 確保同一個 key 僅針對「空陣列」重撈一次
    const retriedEmptyRef = useRef(false);
    useEffect(() => {
        const keyChanged = prevKeyRef.current !== paramKey;
        const emptyParams = parameters?.length === 0;

        // -------- Key 改變：一定重撈並重置 retry 狀態 --------
        if (keyChanged) {
            prevKeyRef.current = paramKey;
            retriedEmptyRef.current = false; // 讓下一次若仍拿到空陣列還能再撈一次
            triggerParams({ def: defId, subdef: subdefIds });
            return;
        }

        // -------- 同一個 key，但拿到空陣列：重撈一次 --------
        if (emptyParams && !retriedEmptyRef.current) {
            retriedEmptyRef.current = true; // 避免無限迴圈
            triggerParams({ def: defId, subdef: subdefIds });
        }
    }, [paramKey, parameters, triggerParams, defId, subdefIds]);

    useEffect(() => {
        const slider = document.querySelector('thead.ant-table-thead tr') as HTMLElement;
        const body = document.querySelector('.ant-table-container .ant-table-body ') as HTMLElement;
        let isDown = false;
        let startX: number;
        let scrollLeft: number;

        if (!slider || !body) return;

        body.addEventListener('mousedown', (e: MouseEvent) => {
            isDown = true;
            slider.classList.add('active');
            startX = e.pageX - body.offsetLeft;
            scrollLeft = body.scrollLeft;
        });

        body.addEventListener('mouseleave', () => {
            isDown = false;
            slider.classList.remove('active');
        });

        body.addEventListener('mouseup', () => {
            isDown = false;
            slider.classList.remove('active');
        });

        body.addEventListener('mousemove', (e: MouseEvent) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - body.offsetLeft;
            const walk = (x - startX) * 1.5;
            body.scrollLeft = scrollLeft - walk;
        });
    }, []);

    useEffect(() => {
        setPage(0);
    }, [rowsPerPage]);

    const handleSortRequest = (columnName: string) => {
        const param = parameters?.find((p) => p.id.toString() === columnName);
        const isNumeric = param?.isNumeric || false;
        setIsNumericSort(isNumeric);

        if (columnName !== 'partNumber') {
            if (descColumn === columnName) {
                setDescColumn('');
                setAscColumn(columnName);
            } else if (ascColumn === columnName) {
                setAscColumn('');
                setDescColumn(columnName);
            } else {
                setAscColumn('');
                setDescColumn(columnName);
            }
        }
        setPage(0);
    };

    const handlePartNumberClick = (partNumber: string) => {
        const currentPath = location.pathname;
        navigate(`${currentPath}/part/${encodeURIComponent(partNumber)}`);
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0); // Reset page number
    };

    const columns = useProductTableColumns({
        locale,
        language,
        parameters,
        ascColumn,
        descColumn,
        compareItemPns,
        onToggleCompareItem,
        multiOctoPartsResponse,
        detectedUniquePartsMap,
        handlePartNumberClick,
        handleSortRequest,
        contentTree,
    });

    // useEffect(() => {
    //     console.log('[ProductTable] columns reference changed', columns);
    // }, [columns]);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Table: {
                        headerBg: '#B75332',
                        headerColor: 'white',
                        headerBorderRadius: 0,
                        cellFontSize: 16,
                        rowHoverBg: '#F2F2F2',
                    },
                    Pagination: {
                        colorFill: '#B75332',
                        colorBgContainer: '#B75332',
                        colorTextLabel: 'white',
                        borderRadius: 0,
                    },
                },
            }}
        >
            <TableWrapper>
                <StyledTable
                    columns={columns}
                    bordered={true}
                    dataSource={sortedBaseParts}
                    loading={parametersLoading || isTableLoading || isPnLoading}
                    tableLayout="auto" // 加上這行
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    sticky={{ offsetHeader: HEADER_TOP }}
                    rowClassName={(_, index) =>
                        // 由於 index 從 0 開始，奇數行使用 .oddRow，偶數行使用 .evenRow
                        index % 2 === 1 ? 'grab-cursor oddRow' : 'grab-cursor evenRow'
                    }
                    rowKey={(record) => record.basePn}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        backgroundColor: 'white',
                    }}
                    data-testid="pagination-bar"
                >
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={detectedUniqueParts ? detectedUniqueParts.length : partCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        ActionsComponent={ActionComponentDisabled}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ marginTop: '0.1rem' }}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from.toLocaleString()}-${to.toLocaleString()} of ${count.toLocaleString()}`
                        }
                        data-testid="pagination-count"
                    />

                    <div
                        style={{
                            margin: '0.8rem 1rem 0 0.5rem',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        |
                    </div>
                    <CustomPagination
                        count={detectedUniqueParts ? detectedUniqueParts.length : partCount}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handleChangePage}
                    />
                </Box>
            </TableWrapper>
        </ConfigProvider>
    );
};

export default ProductTable;
